import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { Straighten } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useMyPersonEntityId } from '@/hooks/useMyPersonEntityId';
import { useEntities } from '@/hooks';
import { PersonSizingDialog } from '@/components/iem/dialogs/PersonSizingDialog';
import type { Entity } from '@/types/Entity';

/**
 * My Sizing — personal sizing view for linked operational users.
 *
 * Reuses the existing PersonSizingDialog (no duplicate component), auto-opened
 * and scoped to the current user's own person entity. Navigates back when the
 * dialog is closed. Implements #346 (additive access for CIF/HR/Comptroller/Finance).
 */
export const MySizingPage: React.FC = () => {
  usePageTitle('My Sizing');
  const navigate = useNavigate();

  const { myPersonEntityId, isLoading: personLoading } = useMyPersonEntityId();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch own person entity
  const { data: entities, isLoading: entitiesLoading } = useEntities({
    type: 'person',
    enabled: !!myPersonEntityId,
  });

  const myPersonEntity: Entity | undefined = myPersonEntityId
    ? entities?.find((e) => e.id === myPersonEntityId)
    : undefined;

  // Open dialog once the entity is resolved
  useEffect(() => {
    if (myPersonEntity) {
      setDialogOpen(true);
    }
  }, [myPersonEntity]);

  const handleClose = () => {
    setDialogOpen(false);
    navigate(-1);
  };

  const isLoading = personLoading || (!!myPersonEntityId && entitiesLoading);

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Fail-closed: no person entity resolved
  if (!myPersonEntityId || !myPersonEntity) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Your account is not linked to a person entity. My Sizing is not available.
        </Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Straighten color="primary" sx={{ fontSize: 36 }} />
        <Typography variant="h4" component="h1">
          My Sizing
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View and update your personal sizing assignments.
      </Typography>

      {/* PersonSizingDialog auto-opened, scoped to own person entity */}
      <PersonSizingDialog
        open={dialogOpen}
        onClose={handleClose}
        person={myPersonEntity}
      />
    </Container>
  );
};
